<template>
  <div
      class="form-optiongroup-checkbox"
      role="group"
      :style="{ '--checkmark-url': `url(${checkmarkIcon})` }"
  >
    <div
        v-for="option in options"
        :key="option.value"
        class="form-option-checkbox"
        :class="{ 'is-selected': modelValue.includes(option.value) }"
    >
      <input
          type="checkbox"
          :value="option.value"
          :id="'power_consumer_' + option.value"
          :checked="modelValue.includes(option.value)"
          @change="() => toggleOption(option.value)"
      />

      <slot name="option" :option="option">
        <label :for="'power_consumer_' + option.value">
          <img v-if="option.image" :src="option.image" alt="" aria-hidden="true" />
          <p>{{ option.name }}</p>
        </label>
      </slot>
    </div>
  </div>
</template>

<script setup>
import {defineProps, defineEmits} from 'vue';
import checkmarkIcon from '@/assets/icons/checkmark.svg';

const props = defineProps({
  options: {type: Array, required: true},
  modelValue: {type: Array, required: true},
});

const emit = defineEmits(['update:modelValue']);

/**
 * Manuelle Funktion zur Handhabung der Checkbox-Änderung.
 * Hier wird die Logik für 'single: true' implementiert.
 */
const toggleOption = (value) => {
  let newValue = [...props.modelValue];
  const index = newValue.indexOf(value);
  const toggledOption = props.options.find(opt => opt.value === value);

  // 1. Standard: Option hinzufügen oder entfernen
  if (index > -1) {
    // Option war gewählt -> entfernen
    newValue.splice(index, 1);
  } else {
    // Option war nicht gewählt -> hinzufügen
    newValue.push(value);
  }

  // 2. Anwenden der Exklusiv-Logik
  if (toggledOption?.single) {
    // Wenn die neu ausgewählte/abgewählte Option exklusiv ist:
    if (index > -1) {
      // Wenn sie ABGEWÄHLT wurde, tue nichts Besonderes (sie wurde bereits oben entfernt)
    } else {
      // Wenn sie AUSGEWÄHLT wurde: Array auf nur diese eine Option setzen
      newValue = [value];
    }
  } else {
    // Wenn die Option NICHT exklusiv ist:
    // Alle aktuell gewählten exklusiven Optionen entfernen, um die Mehrfachauswahl zu erlauben.
    newValue = newValue.filter(v => {
      const opt = props.options.find(o => o.value === v);
      // Behalte nur Optionen, die nicht als 'single: true' markiert sind
      return opt && !opt.single;
    });
  }

  // 3. Senden des Events zurück an die Parent-Komponente
  emit('update:modelValue', newValue);
};
</script>

<style scoped>

/* --- Allgemeine Checkbox-Stile --- */
.form-optiongroup-checkbox {
  display: grid !important;
  gap: 8px;
  padding-bottom: 20px;
  grid-template-columns: repeat(1, 1fr);

  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(min(18%, 600px), 1fr));
  }

  @media (max-width: 1199px) and (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(min(25%, 600px), 1fr));
  }

  @media (max-width: 991px) and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
}

.form-option-checkbox {
  background-color: var(--form-layoutcard-bg-default);
  border: var(--form-layoutcard-border-default);
  border-radius: var(--form-layoutcard-radius-default);
  position: relative;
  outline: none;
  transition: var(--form-layoutcard-hover-transition);

  /* Zustände: Hover & Selektiert */
  /* 1. NUR wenn die Option wirklich ausgewählt ist (gilt für alle Geräte) */
  &.is-selected {
    border: var(--form-layoutcard-border-transparent);
    background-color: var(--form-layoutcard-bg-default);
    box-shadow: inset 0 0 0 2px var(--form-layoutcard-color-selected);
    transition: var(--form-layoutcard-hover-transition);
  }

  /* 2. NUR wenn der User eine echte Maus benutzt (verhindert den iOS-Geister-Rahmen) */
  @media (hover: hover) {
    &:hover {
      border: var(--form-layoutcard-border-transparent);
      background-color: var(--form-layoutcard-bg-default);
      box-shadow: inset 0 0 0 2px var(--form-layoutcard-color-hover);
      transition: var(--form-layoutcard-hover-transition);
    }
    &.is-selected:hover {
      box-shadow: inset 0 0 0 2px var(--form-layoutcard-color-selected);
    }
  }

  &:active,
  &.is-selected:active {
    background-color: var(--form-layoutcard-bg-press);
    box-shadow: inset 0 0 0 1px var(--form-layoutcard-color-press);
    transition: var(--form-layoutcard-active-transition);
  }

  &:has(input[type="checkbox"]:focus-visible) {
    /*box-shadow: none;*/
    outline: var(--form-layoutcard-border-focus);
    outline-offset: var(--form-layoutcard-focus-offset);
    border-radius: var(--form-layoutcard-radius-focus);
  }

  /* Checkbox-Input Verbergen */
  & input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    z-index: -1;
    width: 1px;
    height: 1px;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
}
.form-option-checkbox.is-selected label::after,
  /* Aktiviert den Haken für das externe Label aus dem Slot */
.form-option-checkbox.is-selected :slotted(label)::after {
  display: block !important;
}

/* Layout des Labels (Jetzt sauber auf oberster Ebene nebeneinander) */
.form-option-checkbox label,
.form-option-checkbox :slotted(label) {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  box-sizing: content-box !important;
  height: 175px;
  padding: 20px 44px;
  outline: none;

}

/* Bilder innerhalb des Labels */
.form-option-checkbox label img,
.form-option-checkbox :slotted(label img) {
  height: 100px;
  width: 100px;
  margin-bottom: 16px;
  object-fit: contain;
}

/* Der gelbe Haken
.form-option-checkbox label::after,
.form-option-checkbox :slotted(label)::after {
  content: "✔";
  position: absolute;
  top: 15px;
  right: 15px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--color-yellow);
  display: none;
  align-items: center;
  justify-content: center;
  color: #000;
  font-weight: bold;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

 */
.form-option-checkbox label::after,
.form-option-checkbox :slotted(label)::after {
  content: "";
  position: absolute;
  top: 15px;
  right: 15px;
  width: 28px;
  height: 28px;
  /*border-radius: 50%;*/
  /*background-color: var(--color-yellow);*/
  display: none;
  /*
  align-items: center;
  justify-content: center;
  font-weight: bold;
  */
  /*box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);*/
  background-image: var(--checkmark-url);
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;
}


/* Mobile Anpassung */
@media (max-width: 767px) {
  /* Wir zielen spezifisch auf das slotted Label, um die 175px Höhe komplett zu brechen */
  .form-option-checkbox :slotted(label),
  .form-option-checkbox label {
    padding: 0.6rem 1.2rem !important; /* Gleiches flaches Padding wie bei Radio */
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    height: auto !important; /* Entfernt die Desktop-Höhe komplett */
    min-height: 0 !important; /* Verhindert, dass eine Mindesthöhe das Element aufbläht */
    text-align: left;
  }

  .form-option-checkbox label img,
  .form-option-checkbox :slotted(label img) {
    height: 40px;  /* Gleiche Bildgröße wie bei Radio */
    width: 40px;
    margin-right: 1rem;
    margin-bottom: 0;
    margin-top: 0;
  }

  .form-option-checkbox label p,
  .form-option-checkbox :slotted(label p) {
    font-size: 1em;
    line-height: 1.3;
  }

  /* Das Häkchen auf Mobile leicht anpassen, damit es im flachen Layout gut sitzt */
  .form-option-checkbox label::after,
  .form-option-checkbox :slotted(label)::after {
    top: 50%;
    transform: translateY(-50%); /* Zentriert den Haken vertikal im flachen Balken */
    right: 15px;
  }
}

</style>